
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  ShieldCheck, 
  PenTool, 
  CheckCircle, 
  Download, 
  Loader2, 
  ArrowRight,
  ChevronLeft,
  FileText,
  AlertCircle,
  Calendar,
  Building,
  Clock,
  Printer
} from 'lucide-react';
import { Agreement, BusinessProfile } from '../types';
import { supabase, toCamelCase, getErrorMessage } from '../lib/supabase';

interface SigningPortalProps {
  agreementId: string;
  businessProfile: BusinessProfile;
  onComplete: () => void;
}

const SigningPortal: React.FC<SigningPortalProps> = ({ agreementId, businessProfile, onComplete }) => {
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    if (agreementId) fetchAgreement();
  }, [agreementId]);

  const fetchAgreement = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('agreements').select('*').eq('id', agreementId).maybeSingle();
      if (data) {
        const agr = toCamelCase(data) as Agreement;
        setAgreement(agr);
        if (agr.status === 'Signed') {
          setSigned(true);
          setSignatureData(agr.signatureData || null);
        }
      }
    } catch (err) {
      console.error("Fetch agreement error:", err);
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    isDrawing.current = true;
    draw(e);
  };

  const draw = (e: any) => {
    if (!isDrawing.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;

    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';

    if (e.type === 'mousedown' || e.type === 'touchstart') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    isDrawing.current = false;
    if (canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setSignatureData(null);
    }
  };

  const handleSign = async () => {
    if (!agreement || !signatureData || !contentRef.current) return;
    setSigning(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const signedAtTimestamp = new Date().toISOString();
      const element = contentRef.current;

      // 1.5 Scale is crucial for "Status Unknown" printer fix - avoids massive memory spikes
      const canvas = await html2canvas(element, { 
        scale: 1.5, 
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: 800,
        onclone: (clonedDoc) => {
          const sigBlock = clonedDoc.getElementById('archival-signature-block');
          if (sigBlock) {
            sigBlock.style.display = 'block';
            sigBlock.style.opacity = '1';
          }
        }
      });
      
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.70);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const innerWidth = pdfWidth - (margin * 2);
      const innerHeight = pdfHeight - (margin * 2);
      
      const pxPageHeight = (innerHeight * canvas.width) / innerWidth;
      let leftHeight = canvas.height;
      let positionY = 0;
      let page = 1;

      while (leftHeight > 0) {
        if (page > 1) pdf.addPage();
        
        pdf.addImage(
          imgData, 
          'JPEG', 
          margin, 
          margin - (positionY * innerWidth / canvas.width), 
          innerWidth, 
          (canvas.height * innerWidth / canvas.width),
          undefined,
          'FAST'
        );

        // Margin Masks: Solid white rectangles to frame the page and hide cut-offs
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, pdfHeight - margin, pdfWidth, margin, 'F');
        pdf.rect(0, 0, pdfWidth, margin, 'F');

        // Move cursor, using a tiny 2px overlap to avoid blank gaps in text
        positionY += (pxPageHeight - 2); 
        leftHeight -= (pxPageHeight - 2);
        page++;
        
        if (leftHeight < 15) break;
      }
      
      const pdfBlob = pdf.output('blob');
      const fileName = `VERIFIED_${agreement.title.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      
      const { error: uploadError } = await supabase.storage
        .from('agreements')
        .upload(fileName, pdfBlob, { 
          contentType: 'application/pdf', 
          upsert: true,
          cacheControl: '3600'
        });

      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage.from('agreements').getPublicUrl(fileName);

      const { error: dbError } = await supabase.from('agreements').update({
         status: 'Signed',
         signed_at: signedAtTimestamp,
         signature_data: signatureData,
         signed_file_url: publicUrl
      }).eq('id', agreement.id);

      if (dbError) throw dbError;

      setSigned(true);
      alert("Technical Affirmation Successful. Registry archived.");
      onComplete();
    } catch (err: any) { 
      console.error("Signing sequence failure:", err);
      alert(`Archival Failure: ${getErrorMessage(err)}`); 
    } finally { 
      setSigning(false); 
    }
  };

  if (loading) return (
    <div className="h-screen bg-[#f8faf7] flex flex-col items-center justify-center space-y-4">
      <Loader2 className="animate-spin text-[#606c38]" size={32} />
      <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Accessing Protocol...</p>
    </div>
  );

  if (!agreement) return (
    <div className="h-screen bg-slate-50 flex flex-col items-center justify-center p-8 text-center">
      <AlertCircle size={48} className="text-rose-500 mb-4" />
      <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Access Invalid</h2>
      <button onClick={onComplete} className="mt-6 px-6 py-3 bg-slate-900 text-white rounded-lg text-[9px] font-black uppercase tracking-widest">Return to Gateway</button>
    </div>
  );

  const displayTimestamp = signed ? (agreement.signedAt ? new Date(agreement.signedAt).toLocaleString() : 'Processing...') : new Date().toLocaleString();
  const processedContent = agreement.content.replace(/\[DATE\]/g, new Date().toLocaleDateString());

  return (
    <div className="min-h-screen bg-slate-100 py-4 md:py-8 px-4 md:px-6 font-sans">
      <div className="max-w-[800px] mx-auto">
        <div className="bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-500">
          <div className="p-4 md:p-6 bg-slate-900 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <button onClick={onComplete} className="p-1.5 hover:bg-white/10 rounded-md text-white/50 transition-colors"><ChevronLeft size={22} /></button>
              <div className="min-w-0">
                <h1 className="text-sm font-black uppercase tracking-tight leading-none mb-1">Compliance Portal</h1>
                <p className="text-slate-400 text-[8px] font-mono tracking-widest">REG: {agreement.id.substring(0, 12).toUpperCase()}</p>
              </div>
            </div>
            {signed && (
               <div className="px-3 py-1 bg-emerald-500 text-white rounded text-[8px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-md">
                  <CheckCircle size={12} /> Registry Archived
               </div>
            )}
          </div>

          <div ref={contentRef} className="p-10 md:p-16 lg:p-24 bg-white">
             <div className="mb-12 border-b-2 border-slate-900 pb-8">
                <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2 leading-tight tracking-tight uppercase">{agreement.title}</h2>
                <div className="flex flex-wrap gap-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                   <p className="flex items-center gap-1.5"><FileText size={12} className="text-blue-500" /> REF-SEC-{agreement.inspectionId.substring(0, 8).toUpperCase()}</p>
                   <p className="flex items-center gap-1.5"><Calendar size={12} className="text-[#606c38]" /> {new Date(agreement.createdAt).toLocaleDateString()}</p>
                </div>
             </div>

             <div className="prose prose-slate max-w-none mb-16">
                <div className="text-slate-800 text-[13pt] leading-[1.6] whitespace-pre-wrap font-medium">
                   <ReactMarkdown remarkPlugins={[remarkGfm]}>{processedContent}</ReactMarkdown>
                </div>
             </div>

             {(signed || signatureData) && (
                <div id="archival-signature-block" className="mt-12 pt-10 border-t-2 border-slate-900 w-fit min-w-[300px]">
                   <div className="mb-4">
                      <img src={signatureData || agreement.signatureData} className="h-16 object-contain" alt="Signature" />
                   </div>
                   <div className="space-y-0.5">
                      <p className="text-[11px] font-black uppercase text-slate-900 tracking-widest">Verified Affirmation</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase flex items-center gap-1">
                        <Clock size={10} className="text-slate-300" /> {displayTimestamp}
                      </p>
                   </div>
                </div>
             )}
          </div>

          {!signed && (
            <div className="p-8 md:p-12 border-t border-slate-100 bg-slate-50">
               <div className="max-w-xl">
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Final Affirmation Signature</h4>
                  <div className="bg-white border-2 border-slate-200 rounded-xl p-2 mb-8 shadow-sm overflow-hidden">
                     <canvas 
                        ref={canvasRef} 
                        width={800} 
                        height={250} 
                        className="w-full h-44 cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                     />
                  </div>
                  <div className="flex gap-3">
                     <button onClick={clearSignature} className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 border border-slate-200 rounded-xl transition-all">Clear</button>
                     <button disabled={!signatureData || signing} onClick={handleSign} className="flex-1 py-4 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">
                        {signing ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Secure Protocol Record
                     </button>
                  </div>
               </div>
            </div>
          )}

          {signed && (
            <div className="p-10 md:p-16 bg-white border-t border-slate-100 flex flex-col items-center text-center">
               <div className="p-4 bg-emerald-50 text-emerald-600 rounded-full mb-6">
                  <CheckCircle size={40} />
               </div>
               <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2">Registry Record Finalized</h3>
               <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mb-10">A copy of this affirmation has been archived for all parties.</p>
               
               <div className="flex gap-3 w-full justify-center">
                 <button onClick={() => agreement.signedFileUrl && window.open(agreement.signedFileUrl, '_blank')} className="px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm hover:bg-slate-50 transition-all">
                    <Download size={14} /> Get PDF Record
                 </button>
                 <button onClick={onComplete} className="px-8 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-md hover:bg-slate-800 transition-all">
                    Back to Hub <ArrowRight size={14} />
                 </button>
               </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SigningPortal;
